/**
 * Small local date helpers for the clearance ladder's target-date input.
 *
 * Deliberately not shared with backwards-planner/plan.ts — this tool is built
 * standalone so that tool's file stays untouched. The overlap is a handful of
 * generic date utilities, not enough to justify extracting a shared module and
 * touching an already-shipped tool for it.
 */

const DAY = 24 * 60 * 60 * 1000;

/** Midnight today, in local time. */
export function today(): Date {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

/** A neutral default target the merchant is clearly meant to change: the end
 * of the current year (31 December), the natural "clear it out before the new
 * year" date for post-Black-Friday stock. Falls forward to next year if today
 * is already 31 December. */
export function defaultTargetDate(): Date {
  const t = today();
  const yearEnd = new Date(t.getFullYear(), 11, 31);
  return yearEnd.getTime() > t.getTime()
    ? yearEnd
    : new Date(t.getFullYear() + 1, 11, 31);
}

/** Ordinal suffix for a day of the month: 1 → "st", 2 → "nd", 3 → "rd", etc. */
function ordinalSuffix(day: number): string {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

/** Month-first with an ordinal day, e.g. "December 2nd". */
export function fmtLong(date: Date): string {
  const month = date.toLocaleDateString("en-GB", { month: "long" });
  const day = date.getDate();
  return `${month} ${day}${ordinalSuffix(day)}`;
}

/** Format a Date as the yyyy-mm-dd value a native date input expects. */
export function toDateValue(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}

/** Whole days from `a` to `b` (positive when `b` is later). */
export function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / DAY);
}

/**
 * A calendar date `weeks` (possibly fractional) after `start`, rounded to the
 * nearest whole day — used to resolve a checkpoint's `week` number (from
 * {@link import("@/lib/bf-toolkit/calculations").clearanceLadder}) back to a
 * real date for display.
 */
export function dateFromWeeks(start: Date, weeks: number): Date {
  // Advance by calendar days (not a fixed ms offset) so checkpoints stay on
  // the same local date across DST transitions.
  const result = new Date(start);
  result.setDate(result.getDate() + Math.round(weeks * 7));
  return result;
}
